import { useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEventHandler, ReactNode } from 'react';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import Popper from '@material-ui/core/Popper';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { useDebouncedValue } from '@shiftfinancial/react-hooks';

import styles from './autosuggestinputstyles';

const useStyles = makeStyles(styles as never);

type Placement =
  | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top';

type AutoSuggestInputProps = {
  getSuggestions: (value: string) => void;
  getSuggestionValue?: (suggestion: any) => string;
  onSuggestionSelected?: (e: FormEvent, data: unknown) => void;
  suggestions?: unknown[] | null;
  isGetSuggestionsDone: boolean;
  delay?: number;
  onKeyPress?: KeyboardEventHandler | null;
  onKeyUp?: KeyboardEventHandler | null;
  suggestionsPlacement?: Placement;
  autoFocus?: boolean;
  fieldValue?: string | object | null;
};

function AutoSuggestInput({
  getSuggestions,
  getSuggestionValue = () => '',
  onSuggestionSelected = () => {},
  suggestions = null,
  isGetSuggestionsDone,
  delay = 500,
  onKeyPress = null,
  onKeyUp = null,
  suggestionsPlacement = 'bottom',
  autoFocus = false,
  fieldValue = null,
}: AutoSuggestInputProps) {
  const classes = useStyles();
  const [popper, setPopper] = useState('');
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [stateSuggestions, setStateSuggestions] = useState<unknown[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const debouncedSearchValue = useDebouncedValue(searchValue, delay);

  const anchorRef = useRef<HTMLElement | null>(null);

  const prevFieldValue = useRef(fieldValue);
  if (prevFieldValue.current !== fieldValue) {
    if (fieldValue === null && prevFieldValue.current !== null) {
      setPopper('');
      setLastQuery(null);
      setStateSuggestions([]);
    }
    prevFieldValue.current = fieldValue;
  }

  const getSuggestionsRef = useRef(getSuggestions);
  useEffect(() => {
    getSuggestionsRef.current = getSuggestions;
  });

  useEffect(() => {
    if (debouncedSearchValue !== null) {
      getSuggestionsRef.current(debouncedSearchValue);
    }
  }, [debouncedSearchValue]);

  const handleChange = (_e: FormEvent, { newValue }: { newValue: string }) => {
    setPopper(newValue);
  };

  const handleSuggestionsFetchRequested = ({
    value,
    reason,
  }: {
    value: string;
    reason: string;
  }) => {
    if (reason !== 'input-focused') {
      setSearchValue(value);
    }
  };

  const handleSuggestionsClearRequested = () => {
    setStateSuggestions([]);
  };

  const handleSuggestionSelected = (e: FormEvent, data: { suggestion: unknown }) => {
    const query = getSuggestionValue(data.suggestion);
    onSuggestionSelected(e, data);
    setLastQuery(query);
  };

  return (
    <div className={classes.root}>
      <Autosuggest
        data-testid="autoSuggest"
        renderInputComponent={renderInputComponent}
        suggestions={suggestions || stateSuggestions}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={(
          suggestion: unknown,
          params: { query: string; isHighlighted: boolean },
        ) => {
          const displayText = getSuggestionValue!(suggestion);
          return renderSuggestion(displayText, params, classes);
        }}
        inputProps={{
          id: 'text-autosuggest',
          value: popper,
          onChange: handleChange,
          onKeyPress: onKeyPress ?? undefined,
          onKeyUp: onKeyUp ?? undefined,
          autoFocus,
          inputRef: (node: HTMLElement) => {
            anchorRef.current = node;
          },
        }}
        theme={{
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderSuggestionsContainer={(options: {
          query: string;
          children: ReactNode;
          containerProps: Record<string, unknown>;
        }) => {
          if (lastQuery === options.query) {
            return null;
          }

          return (
            <Popper
              anchorEl={anchorRef.current}
              open={Boolean(options.children)}
              placement={suggestionsPlacement}
              className={classes.suggestionsContainerOpen}
              data-testid="autoSuggestItemContainer"
            >
              <Paper
                square
                {...options.containerProps}
                style={{ width: anchorRef.current ? anchorRef.current.clientWidth : undefined }}
              >
                {options.children}
              </Paper>
            </Popper>
          );
        }}
      />
      {!isGetSuggestionsDone && (
        <div
          className={classes.spinnerContainer}
          style={{ width: anchorRef.current ? anchorRef.current.clientWidth : undefined }}
        >
          <CircularProgress className={classes.spinner} disableShrink size={40} />
        </div>
      )}
    </div>
  );
}

function renderSuggestion(
  displayText: string,
  { query, isHighlighted }: { query: string; isHighlighted: boolean },
  classes: Record<string, string>,
) {
  const matches = match(displayText, query);
  const parts = parse(displayText, matches);

  return (
    <MenuItem
      selected={isHighlighted}
      component="div"
      key={query + displayText}
      className={classes.suggestionText}
    >
      <div>
        {parts.map((part: { text: string; highlight: boolean }, i: number) => (
          <span key={part.text + i} style={{ fontWeight: part.highlight ? 500 : 400 }}>
            {part.text}
          </span>
        ))}
      </div>
    </MenuItem>
  );
}

function renderInputComponent(inputProps: Record<string, unknown>) {
  const { inputRef = () => {}, ref, ...other } = inputProps;

  return (
    <TextField
      fullWidth
      variant="outlined"
      InputProps={{
        inputRef: (node: HTMLElement) => {
          (ref as (node: HTMLElement) => void)(node);
          (inputRef as (node: HTMLElement) => void)(node);
        },
      }}
      {...other}
    />
  );
}

export default AutoSuggestInput;
